//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region Angular Imports

import { StateService } from '@uirouter/angularjs'

//#endregion

import { Enums } from '../../models/common'
import { CreateEditInitParams } from '../../modules/common/create-edit-popup'

import { Dictionary } from '../../helpers/dictionary'
import { TypeHelper, LogHelper } from '../../helpers'

import { DeviceService, DEVICE_SERVICE_NAME } from '../device'
import { LocationService, LOCATION_SERVICE_NAME } from '../location'
import { MeterService, METER_SERVICE_NAME } from '../meter'
import { UserSessionService, USER_SESSION_SERVICE_NAME } from '../user-session'

import { SystemService, SERVICE_NAME } from './system.service'
import { $STATE_SERVICE_NAME } from './system.service.interfaces'

// #region Public Types

export const SYSTEM_TARGET_SERVICE_NAME = 'SystemTargetService'
export const SYSTEM_TARGET_PROVIDER_NAME = 'SystemTargetServiceProvider'

//#endregion


//#region Private Types

type OpenTargetArgs = {
    target: string,
    args: Dictionary<string>
}

//#endregion

export class SystemTargetService {

    //#region Consts

    private get LOG_PREFIX(): string { return '[SIMAX.Services.System.Target]' }

    //#endregion


    //#region Ctor

    constructor(
        private $state: StateService,
        private deviceService: DeviceService,
        private locationService: LocationService,
        private meterService: MeterService,
        private systemService: SystemService,
        private userSessionService: UserSessionService
    ) { }

    //#endregion


    public openTarget(args: string): boolean {
        if (args != null) {
            LogHelper.log(this.LOG_PREFIX, `Tyring to open target with args: ${args}.`)

            const parsedArgs: OpenTargetArgs = this.parseOpenTargetArgs(args)

            if (parsedArgs.target != null) {
                const simaxModuleName: Enums.SimaxModuleName = this.getModuleNameFromTargetParam(parsedArgs.target)

                // Jałowizna
                if (simaxModuleName === Enums.SimaxModuleName.Meter_Alarm) {
                    return true
                }

                if (simaxModuleName != null) {
                    if (parsedArgs.target.search("Details") > 0) {
                        this.openDetails(simaxModuleName, parsedArgs.args)
                    }
                    else if (parsedArgs.target.search("Edit") > 0) {
                        this.openEdit(simaxModuleName, parsedArgs.args)
                    }
                    else if (parsedArgs.target.search("Module") > 0) {
                        this.openModule(parsedArgs.target)
                    }
                    else {
                        LogHelper.warn(this.LOG_PREFIX, `Unable to handle the given target.`)
                    }
                }
                else {
                    LogHelper.warn(this.LOG_PREFIX, `Unable to get the module name from the given args.`)
                }
            } else {
                LogHelper.warn(this.LOG_PREFIX, `Unable to get the target from the given args.`)
            }
        }
    }

    private openEdit(moduleName: Enums.SimaxModuleName, args: Dictionary<string>): void {
        const referenceObjectId: number = this.getReferenceObjectIdFromArgs(args)

        if (referenceObjectId != null) {
            switch (moduleName) {
                case Enums.SimaxModuleName.Location:
                    this.locationService.getCreateEditOptions(referenceObjectId)
                        .then((createEditOptions: CreateEditInitParams) => {
                            this.systemService.openCreateEdit(createEditOptions)
                        })

                    break

                case Enums.SimaxModuleName.Device:
                    this.systemService.openCreateEdit(this.deviceService.getCreateEditOptions(this.userSessionService, referenceObjectId))

                    break

                case Enums.SimaxModuleName.Meter:
                    this.systemService.openCreateEdit(this.meterService.getCreateEditOptions(referenceObjectId))

                    break
            }
        }
        else {
            LogHelper.warn(this.LOG_PREFIX, `Unable to open edit from the given args.`)
        }
    }

    private openDetails(moduleName: Enums.SimaxModuleName, args: Dictionary<string>): void {
        const activeTab: string = args.tryGetValue('activeTab')
        const activeAccordion: string = args.tryGetValue('activeAccordion')
        const referenceObjectId: number = this.getReferenceObjectIdFromArgs(args)

        if (referenceObjectId != null) {
            this.systemService.openDetails({
                moduleName: moduleName,
                referenceObjectId,
                activeTab: activeTab != null ? this.getNameBySchemaParam(activeTab) : null,
                activeAccordion: activeAccordion != null ? this.getNameBySchemaParam(activeAccordion) : null
            })
        } else {
            LogHelper.warn(this.LOG_PREFIX, `Unable to open details from the given args.`)
        }
    }

    private openModule(target: string): void {
        const stateName: string = this.getStateNameByTargetName(target)

        if (stateName != null) {
            this.$state.go(stateName, { ids: [] })
        } else {
            LogHelper.warn(this.LOG_PREFIX, `Unable to open module from the given args.`)
        }
    }

    private parseOpenTargetArgs(args: string): OpenTargetArgs {
        const result: OpenTargetArgs = {
            target: null,
            args: new Dictionary<string>(null)
        }

        const params: string[] = args.trim().split('-')

        _.forEach(params, (param: string) => {
            const innerParams: string[] = param.trim().split(' ')

            switch (_.first(innerParams)) {
                case 'target':
                    if (!TypeHelper.isNullOrEmpty(innerParams[1])) {
                        result.target = innerParams[1].trim()
                    }

                    return

                case 'args':
                    _.forEach(innerParams, (innerParam: string, index: number) => {
                        if (index > 0) {
                            if (!TypeHelper.isNullOrEmpty(innerParam)) {
                                const arg: string[] = innerParam.trim().split('=')
                                const key: string = arg[0]

                                if (!TypeHelper.isNullOrEmpty(key)) {
                                    const value: string = arg[1] == null ? null : arg[1].trim()

                                    result.args.add(key, value)
                                }
                            }
                        }
                    })

                    return
            }

        })

        return result
    }

    private getReferenceObjectIdFromArgs(args: Dictionary<string>): number {
        const SerialNbr: string = args.tryGetValue('SerialNbr')
        const IdMeter: string = args.tryGetValue('IdMeter')
        const IdLocation: string = args.tryGetValue('IdLocation')
        const Id: string = args.tryGetValue('Id')

        const referenceObjectId: number = Number(SerialNbr || IdMeter || IdLocation || Id)

        if (_.isFinite(referenceObjectId)) {
            return referenceObjectId
        }

        return null
    }

    private getModuleNameFromTargetParam(name: string): Enums.SimaxModuleName {
        if (name != null) {
            let swName: string = name.toLowerCase().trim()

            if (swName.indexOf("module") > 0) {
                swName = swName.slice(0, swName.indexOf("module"))
            }
            else if (swName.indexOf("details") > 0) {
                swName = swName.slice(0, swName.indexOf("details"))
            }
            else if (swName.indexOf("edit") > 0) {
                swName = swName.slice(0, swName.indexOf("edit"))
            }

            switch (swName) {
                case 'actionsaction': return Enums.SimaxModuleName.Action
                case 'alarmgroups': return Enums.SimaxModuleName.Alarm_Group
                case 'alarm': return Enums.SimaxModuleName.Alarm
                case 'contact': return Enums.SimaxModuleName.Actor
                case 'deviceconnection': return Enums.SimaxModuleName.Device_Connection
                case 'device': return Enums.SimaxModuleName.Device
                case 'distributor': return Enums.SimaxModuleName.Distributor
                case 'issue': return Enums.SimaxModuleName.Issue
                case 'location': return Enums.SimaxModuleName.Location
                case 'meteralarmdef': return Enums.SimaxModuleName.Meter_Alarm
                case 'meter': return Enums.SimaxModuleName.Meter
                case 'report': return Enums.SimaxModuleName.Report
                case 'route': return Enums.SimaxModuleName.Route
                case 'simcard': return Enums.SimaxModuleName.Sim_Card
                case 'task': return Enums.SimaxModuleName.Task
                case 'operator': return Enums.SimaxModuleName.Operator
                case 'meterdef': return Enums.SimaxModuleName.Meter_Alarm
            }
        }

        return null
    }

    private getNameBySchemaParam(param: string) {
        switch (param.toLowerCase().trim()) {
            // Device
            // tabs
            case 'devicegeneraltab':
            case 'devicemainltab': return 'common'
            case 'devicemeasurementtab': return 'measurements'
            case 'devicehistorytab': return 'history'
            // accordions
            case 'deviceparametersaccordion': return 'params'
            case 'deviceextendedparametersaccordion': return 'specifics'
            case 'deviceparentdevicesaccordion': return 'parentDevices'
            case 'devicechilddevicesaccordion': return 'childDevices'

            // Location
            // tabs
            case 'locationmainltab': return 'common'                     // brak enum
            case 'locationmeasurementtab': return 'measurements'
            case 'locationhistorytab': return 'history'
            case 'locationschematab':
            case 'locationgeneralschematab': return 'Schema'
            // accordions           
            case 'locationparametersaccordion': return 'params'
            case 'locationmeasurementaccordion': return 'measurements'
            case 'locationgroupsaccordion': return 'groups'
            case 'locationdevicesaccordion':
            case 'locationinstalleddevicesaccordion': return 'devices'
            case 'locationmetersaccordion':                              // brak enum
            case 'locationinstalledmetersaccordion': return 'meters'
            case 'locationmeasurementtableaccordion':                   // brak enum
            case 'locationmeasurementpanelaccordion': return 'measurements-table'
            case 'locationmapaccordion': return 'maps'
            case 'locationextendedparametersaccordion': return 'specifics'
            case 'locationconfigurationprofileaccordion': return 'configurationProfile'

            // Meter
            // tabs
            case 'metermaintab': return 'common'                     // brak enum
            case 'metermeasurementtab': return 'measurements'       // brak enum
            case 'meterhistorytab': return 'history'
            // accordions 
            case 'meterparametersaccordion': return 'params'
            case 'meterextendedparametersaccordion': return 'specifics'
            case 'meterdevicesaccordion': return 'devices'
            case 'meteralarmdefaccordion': return 'alarmDefs'
            case 'meteralarmgroupaccordion': return 'alarmGroups'
        }
    }

    private getStateNameByTargetName(name: string) {
        let swName: string = name.toLowerCase().trim()

        if (swName.indexOf("module") > 0) {
            swName = swName.slice(0, swName.indexOf("module"))
        }
        else if (swName.indexOf("details") > 0) {
            swName = swName.slice(0, swName.indexOf("details"))
        }
        else if (swName.indexOf("edit") > 0) {
            swName = swName.slice(0, swName.indexOf("edit"))
        }

        switch (swName) {
            case 'actionsaction': return 'actions'
            case 'alarmgroups': return 'alarm_groups'
            case 'alarm': return 'alarms'
            case 'contact': return 'actors'
            case 'deviceconnection': return 'deviceconnections'
            case 'device': return 'devices'
            case 'distributor': return 'distributors'
            case 'issue': return 'issues'
            case 'location': return 'locations'
            case 'meteralarmdef': return 'meterdefs'
            case 'meter': return 'meters'
            case 'report': return 'reports'
            case 'route': return 'routes'
            case 'simcard': return 'simcards'
            case 'task': return 'tasks'
            case 'operator': return 'operators'
            case 'meterdef': return 'meterdefs'
        }

        return null
    }
}

export class SystemTargetServiceProvider implements ng.IServiceProvider {

    // #region IServiceProvider Implementation 

    public $get = [
        $STATE_SERVICE_NAME,
        DEVICE_SERVICE_NAME,
        LOCATION_SERVICE_NAME,
        METER_SERVICE_NAME,
        SERVICE_NAME,
        USER_SESSION_SERVICE_NAME,
        function (
            $state: StateService,
            deviceService: DeviceService,
            locationService: LocationService,
            meterService: MeterService,
            systemService: SystemService,
            userSessionService: UserSessionService
        ) {
            return new SystemTargetService(
                $state,
                deviceService,
                locationService,
                meterService,
                systemService,
                userSessionService
            )
        }]

    // #endregion
} 